import { AnalyticsData } from '../../Types/AnalyticsGraph';

export interface BenchmarkDataset {
    id: string;
    label: string;
    type: string;
    unit: string;
    data: Array<{ timestamp: string; value: number }>;
    metadata: any;
    isBenchmark: boolean;
}

export interface ProcessedBenchmarkData {
    datasets: BenchmarkDataset[];
    queryDetails: {
        comparisonTargetType?: string;
        averagingPeriodDays?: number;
        filtersApplied?: string[];
    };
}

/**
 * Processes benchmark data from the API response
 */
export const processBenchmarkData = (benchmarkData: any): ProcessedBenchmarkData | null => {
    if (!benchmarkData?.datasets || !Array.isArray(benchmarkData.datasets)) {
        return null;
    }

    const processedDatasets: BenchmarkDataset[] = benchmarkData.datasets.map((dataset: any) => ({
        ...dataset,
        id: `${dataset.id}_benchmark`,
        label: `${dataset.label} (Benchmark)`,
        isBenchmark: true
    }));

    return {
        datasets: processedDatasets,
        queryDetails: benchmarkData.queryDetails || {}
    };
};

/**
 * Merges regular data with benchmark data for chart display
 */
export const mergeBenchmarkData = (
    regularData: AnalyticsData | null,
    benchmarkData: ProcessedBenchmarkData | null,
    showBenchmark: boolean
): AnalyticsData | null => {
    if (!regularData) {
        return null;
    }

    if (!showBenchmark || !benchmarkData) {
        return regularData;
    }

    return {
        ...regularData,
        datasets: [
            ...regularData.datasets,
            ...benchmarkData.datasets
        ]
    };
};

/**
 * Gets the appropriate color for benchmark data based on the original metric color
 */
export const getBenchmarkColor = (originalColor: string): string => {
    // Convert the original color to a more muted/dashed version for benchmark
    // This is a simple implementation - could be enhanced with proper color manipulation
    const colorMap: { [key: string]: string } = {
        '#8884d8': '#b8b8e8',
        '#82ca9d': '#a2dab8',
        '#ffc658': '#ffd584',
        '#ff7300': '#ff9540',
        '#8dd1e1': '#b0dde8',
        '#d084d0': '#e0a8e0',
        '#ffb347': '#ffc973'
    };
    
    return colorMap[originalColor] || `${originalColor}80`; // Fallback: add transparency
};

/**
 * Checks if benchmark data is available for the current metrics
 */
export const hasBenchmarkData = (benchmarkData: any): boolean => {
    return !!(benchmarkData?.datasets && Array.isArray(benchmarkData.datasets) && benchmarkData.datasets.length > 0);
};

/**
 * Gets benchmark metrics count
 */
export const getBenchmarkMetricsCount = (benchmarkData: any): number => {
    if (!hasBenchmarkData(benchmarkData)) {
        return 0;
    }
    return benchmarkData.datasets.length;
};
